// App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, updateDoc, increment } from "firebase/firestore";
import PDFViewer from "./PDFViewer";
import ResumeLibrary from "./ResumeLibrary";
import ScrollToTop from "./components/ScrollToTop";

//SEO Imports
import AIResumeBuilder from "./pages/AIResumeBuilder";
import AIInterviewCoach from "./pages/AIInterviewCoach";
import AICareerAdvisor from "./pages/AICareerAdvisor";
import BestResumeFormat from "./pages/BestResumeFormat";
import ResumeMistakes from "./pages/ResumeMistakes";
import PassInterview from "./pages/PassInterview";
import GetJobFast from "./pages/GetJobFast";
import CareerChangeResume from "./pages/CareerChangeResume";
import ResumeSummaryExamples from "./pages/ResumeSummaryExamples";
import InterviewQuestions from "./pages/InterviewQuestions";
import ResumeSkills from "./pages/ResumeSkills";
import ResumeExamples from "./pages/ResumeExamples";
import TellMeAboutYourself from "./pages/TellMeAboutYourself";
import CollegeCareerPaths from "./pages/CollegeCareerPaths";
import Recruiters from "./pages/Recruiters";
import NoExperienceResume from "./pages/NoExperienceResume";
import CoverLetterGuide from "./pages/CoverLetterGuide";
import EntryLevelResumeExamples from "./pages/EntryLevelResumeExamples";
import NoExperienceJobs from "./pages/NoExperienceJobs";
import FirstJobGuide from "./pages/FirstJobGuide";
import GetStarted from "./pages/GetStarted";
import CareerCommunityInsights from "./pages/CareerCommunityInsights";
import ResumeBuilder from "./pages/ResumeBuilder";
import Resume from "./pages/Resume";
import CoverLetter from "./pages/CoverLetter";
import SEOArticlePage from "./pages/SEOArticlePage";


// Global UI
import Navbar from "./Navbar";

// Public pages
import Dashboard from "./Dashboard";
import AuthPage from "./AuthPage";
import VerifyEmail from "./VerifyEmail";
import Pricing from "./Pricing";
import Success from "./Success";
import Cancel from "./Cancel";
import Terms from "./Terms";
import Privacy from "./Privacy";
import Contact from "./Contact";
import AccountSettings from "./AccountSettings";


import Delete from "./Delete";

// Pro-only wrapper
import ProRoute from "./ProRoute";

// Community pages (PRO ONLY)
import CommunityHome from "./community/CommunityHome";
import ChatRoom from "./community/ChatRoom";
import Forum from "./community/Forum";
import NewPost from "./community/NewPost";
import Thread from "./community/Thread";
import TopicPage from "./community/TopicPage";

// AI Tools (PRO ONLY)
import AITools from "./community/AITools";
import ResumeCritique from "./community/ai/ResumeCritique";
import InterviewCoach from "./community/ai/InterviewCoach";
import CareerAdvisor from "./community/ai/CareerAdvisor";
import LinkedInGenerator from "./community/ai/LinkedInGenerator";
import CoverLetterGenerator from "./community/ai/CoverLetterGenerator";



window.onStoreKitPurchaseSuccess = function (productId) {
  setTimeout(async () => {
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);

    if (productId === "com.employpilot.quick.resume") {
      await updateDoc(userRef, {
        credits: increment(1),
        plan: "quick",
        entitlementsUpdatedAt: new Date(),
      });
    }

    if (productId === "com.employpilot.pro.monthly") {
      await updateDoc(userRef, {
        plan: "pro",
        isPro: true,
        entitlementsUpdatedAt: new Date(),
      });
    }

    // 🔥 NEW: Redirect after success
if (window.location.pathname === "/pricing") {
  window.location.replace("/");
}
  });
}
// ===============================
// iOS WKWebView Native Bridge
// ===============================

window.enableIOSPushNotifications = function () {
  if (
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.enableNotifications
  ) {
    window.webkit.messageHandlers.enableNotifications.postMessage({});
  } else {
    console.log("Enable Notifications: not running inside iOS app");
  }
};



function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Do NOT overwrite previous route when entering Account Settings
    if (location.pathname !== "/account") {
      sessionStorage.setItem(
        "previousRoute",
        location.pathname + location.search
      );
    }
  }, [location]);

  return null;
}


window.openIOSSettings = function () {
  if (
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.openSettings
  ) {
    window.webkit.messageHandlers.openSettings.postMessage({});
  } else {
    // Fallback for web / Android
    window.location.href = "/account";
  }
};

window.closeIOSSettings = function () {
  const previousRoute = sessionStorage.getItem("previousRoute");

  if (previousRoute) {
    window.location.replace(previousRoute);
  } else {
    window.location.replace("/");
  }
};

function AppLayout({ user }) {
  const location = useLocation();

  const hideNavbarRoutes = ["/auth", "/verify-email"];
  const shouldHideNavbar = hideNavbarRoutes.includes(location.pathname);

  return (
    <>
      {!shouldHideNavbar && (
        <Navbar
          user={user}
          onCommunityClick={() => {
            window.dispatchEvent(new Event("open-community-rules"));
          }}
        />
      )}

      <ScrollToTop />

      {/* 👇 ROUTES WILL GO HERE */}
      <Routes>

        {/* ====================================
                 PUBLIC ROUTES
        ==================================== */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cancel" element={<Cancel />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
        <Route
         path="/account"
         element={user ? <AccountSettings /> : <Navigate to="/auth" replace />}
        />
        <Route path="/support/delete-account" element={<Delete />} />
        <Route path="/pdf-viewer" element={<PDFViewer />} />
        <Route
         path="/resume-library"
         element={
        <ProRoute user={user}>
        <ResumeLibrary />
        </ProRoute>
        }
        />


          {/*===================================================
          | | | | | SEO PUBLIC ROUTES
          =====================================================*/}
        <Route path="/ai-resume-builder" element={<AIResumeBuilder />} />
        <Route path="/ai-interview-coach" element={<AIInterviewCoach />} />
        <Route path="/ai-career-advisor" element={<AICareerAdvisor />} />
        <Route path="/best-resume-format" element={<BestResumeFormat />} />
        <Route path="/resume-mistakes" element={<ResumeMistakes />} />
        <Route path="/pass-interview" element={<PassInterview />} />
        <Route path="/get-job-fast" element={<GetJobFast />} />
        <Route path="/career-change-resume" element={<CareerChangeResume />} />
        <Route path="/resume-summary-examples" element={<ResumeSummaryExamples />} />
        <Route path="/interview-questions" element={<InterviewQuestions />} />
        <Route path="/resume-skills" element={<ResumeSkills />} />
        <Route path="/resume-examples" element={<ResumeExamples />} />
        <Route path="/tell-me-about-yourself" element={<TellMeAboutYourself />} />
        <Route path="/college-career-paths" element={<CollegeCareerPaths />} />
        <Route path="/recruiters" element={<Recruiters />} />
        <Route path="/no-experience-resume" element={<NoExperienceResume />} />
        <Route path="/cover-letter-guide" element={<CoverLetterGuide />} />
        <Route path="/entry-level-resume-examples" element={<EntryLevelResumeExamples />} />
        <Route path="/no-experience-jobs" element={<NoExperienceJobs />} />
        <Route path="/first-job-guide" element={<FirstJobGuide />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/career-community-insights" element={<CareerCommunityInsights />} />
        <Route path="/resume-builder" element={<ResumeBuilder />} />
        <Route path="/resume" element={<Resume />} />
        <Route path="/cover-letter" element={<CoverLetter />} />
        <Route path="/:slug" element={<SEOArticlePage />} />

        {/* ====================================
                 DASHBOARD (FREE OR PRO)
        ==================================== */}
        <Route
          path="/"
          element={
            user ? (
              user.emailVerified ? (
                <Dashboard />
              ) : (
                <Navigate to="/verify-email" replace />
              )
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />

        {/* ====================================
                 COMMUNITY (PRO ONLY)
        ==================================== */}
        <Route
          path="/community"
          element={
            user ? (
              user.emailVerified ? (
                <CommunityHome />
              ) : (
                <Navigate to="/verify-email" replace />
              )
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />


        <Route
          path="/community/chat"
          element={
            <ProRoute user={user}>
              <ChatRoom />
            </ProRoute>
          }
        />

 
        <Route
          path="/community/forum"
          element={
            <ProRoute user={user}>
              <Forum />
            </ProRoute>
          }
        />

 
        <Route
          path="/community/new-post"
          element={
            <ProRoute user={user}>
              <NewPost />
            </ProRoute>
          }
        />

 
        <Route
          path="/community/thread/:id"
          element={
            <ProRoute user={user}>
              <Thread />
            </ProRoute>
          }
        />

        <Route
          path="/community/topic/:topicId"
          element={
            <ProRoute user={user}>
              <TopicPage />
            </ProRoute>
          }
        />

        {/* ====================================
                 AI TOOLS (PRO ONLY)
        ==================================== */}
        <Route
          path="/community/tools"
          element={
            <ProRoute user={user}>
              <AITools />
            </ProRoute>
          }
        />

        {/* ====================================
                 COVER LETTER BUILDER (PRO ONLY)
        ==================================== */}
        <Route
          path="/community/tools/cover-letter-generator"
          element={
            <ProRoute user={user}>
              <CoverLetterGenerator />
            </ProRoute>
          }
        />

  
        <Route
          path="/community/tools/resume-critique"
          element={
            <ProRoute user={user}>
              <ResumeCritique />
            </ProRoute>
          }
        />

        <Route
          path="/community/tools/interview-coach"
          element={
            <ProRoute user={user}>
              <InterviewCoach />
            </ProRoute>
          }
        />

        <Route
          path="/community/tools/career-advisor"
          element={
            <ProRoute user={user}>
              <CareerAdvisor />
            </ProRoute>
          }
        />

        {/* ====================================
                 LINKEDIN GENERATOR (PRO ONLY)
        ==================================== */}
        <Route
          path="/community/tools/linkedin-generator"
          element={
            <ProRoute user={user}>
              <LinkedInGenerator />
            </ProRoute>
          }
        />

        {/* ====================================
                 404 FALLBACK
        ==================================== */}
        <Route
          path="*"
          element={
            <div style={{ textAlign: "center", marginTop: "100px" }}>
              <h2>Page Not Found</h2>
              <p>The page you're looking for doesn't exist.</p>
              <a href="/" style={{ color: "#007bff" }}>Go Home</a>
            </div>
          }
        />

      </Routes>
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // =============================
  // AUTH LISTENER
  // =============================
  useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
    setUser(currentUser);
    setLoading(false);

    // 🔥 Send UID to Android native layer
    if (window.AndroidBilling && currentUser?.uid) {
      window.AndroidBilling.registerFcmUid(currentUser.uid);
    }
  });

  return () => unsubscribe();
}, []);

  if (loading) {
  return <div>Initializing EmployPilot...</div>;
}

  return (
  <Router>
    <RouteTracker />
    <AppLayout user={user} />
  </Router>
);
}

export default App;
